import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import {
    setTriageOwners,
    setTriageReporters,
    setTriageSources,
    setTriageStatuses,
    setTriageVersions,
} from '@/actions/triage';
import { IssueSourceType, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectReleasesForAppId, selectTriageFilters } from '@/selectors/selectors';
import { selectAppOrgUsers, selectSortedGroups } from '@/selectors/memoizedSelectors';
import {
    displayTextForIssueSourceType,
    displayTextForIssueState,
    displayTextForUserChecklistGroup,
    displayUserNameLong,
} from '@/helpers/displayHelpers';
import { antGrey } from '@/components/styles/globalStyles';

const STATUS_OPTIONS: IssueState[] = [
    IssueState.New,
    IssueState.Reopened,
    IssueState.FixRequested,
    IssueState.FixApproved,
    IssueState.Assigned,
    IssueState.FixMerged,
    IssueState.FixRejected,
    IssueState.NotAPriority,
];

const SOURCE_OPTIONS: IssueSourceType[] = [
    IssueSourceType.Runway,
    IssueSourceType.PMTicket,
    IssueSourceType.UserReviews,
    IssueSourceType.BetaFeedback,
    IssueSourceType.RegressionTesting,
    IssueSourceType.HealthMetrics,
];

interface TriageFiltersFormValues {
    statuses: IssueState[];
    sources: IssueSourceType[];
    owners: string[];
    reporters: string[];
    versions: string[];
}

export const TriageFilters: React.FC<{
    orgId: string;
    appId: string;
    showVersionFilter: boolean;
    onApply: () => void;
}> = ({ orgId, appId, showVersionFilter, onApply }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<TriageFiltersFormValues>();
    const filters = useSelector((state: RootState) => selectTriageFilters(state, appId));
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, appId));
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));

    const userOptions = appUsers.map((user) => ({ value: user.id, label: displayUserNameLong(user) }));
    const ownerOptions = [
        ...userOptions,
        ...groups.map((group) => ({ value: group.id, label: displayTextForUserChecklistGroup(group) })),
    ];
    const versionOptions = releases.map((release) => ({
        value: release.version,
        label: release.displayableVersion || release.version,
    }));

    const applyFilters = (values: TriageFiltersFormValues) => {
        dispatch(setTriageStatuses({ appId, statuses: values.statuses ?? [] }));
        dispatch(setTriageSources({ appId, sources: values.sources ?? [] }));
        dispatch(setTriageOwners({ appId, owners: values.owners ?? [] }));
        dispatch(setTriageReporters({ appId, reporters: values.reporters ?? [] }));
        // Only touch the Version filter when it's the active version source (no Live/Next / release scope).
        if (showVersionFilter) {
            dispatch(setTriageVersions({ appId, versions: values.versions ?? [] }));
        }
        onApply();
    };

    const clearFilters = () => {
        form.setFieldsValue({ statuses: [], sources: [], owners: [], reporters: [], versions: [] });
        dispatch(setTriageStatuses({ appId, statuses: [] }));
        dispatch(setTriageSources({ appId, sources: [] }));
        dispatch(setTriageOwners({ appId, owners: [] }));
        dispatch(setTriageReporters({ appId, reporters: [] }));
        if (showVersionFilter) {
            dispatch(setTriageVersions({ appId, versions: [] }));
        }
        onApply();
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 320 }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${antGrey.grey4}` }}>
                <Text
                    style={{
                        color: antGrey.grey7,
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                    }}
                >
                    Filters
                </Text>
            </div>
            <div style={{ padding: 16 }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={applyFilters}
                    preserve={false}
                    initialValues={{
                        statuses: filters.statuses,
                        sources: filters.sources,
                        owners: filters.owners,
                        reporters: filters.reporters,
                        versions: filters.versions,
                    }}
                >
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Form.Item name="statuses" label="Status" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select statuses"
                                optionFilterProp="label"
                                maxTagCount={3}
                                options={STATUS_OPTIONS.map((status) => ({
                                    value: status,
                                    label: displayTextForIssueState(status),
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="sources" label="Source" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select sources"
                                optionFilterProp="label"
                                maxTagCount={3}
                                options={SOURCE_OPTIONS.map((source) => ({
                                    value: source,
                                    label: displayTextForIssueSourceType(source),
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="owners" label="Owner" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select owners"
                                optionFilterProp="label"
                                maxTagCount={3}
                                options={ownerOptions}
                            />
                        </Form.Item>
                        <Form.Item name="reporters" label="Reporter" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select reporters"
                                optionFilterProp="label"
                                maxTagCount={3}
                                options={userOptions}
                            />
                        </Form.Item>
                        {showVersionFilter && (
                            <Form.Item name="versions" label="Version" style={{ marginBottom: 0 }}>
                                <Select
                                    allowClear
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select versions"
                                    optionFilterProp="label"
                                    maxTagCount={3}
                                    options={versionOptions}
                                />
                            </Form.Item>
                        )}
                    </Space>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearFilters}>
                    Clear
                </Button>
                <Button size="small" type="primary" onClick={form.submit}>
                    Apply
                </Button>
            </div>
        </div>
    );
};
